import React, { useEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';

import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  FetchResult,
  from,
  HttpLink,
  InMemoryCache,
  Observable,
  Operation
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { GlobalLoader } from '@common/components';
import { DrawerProvider } from '@common/drawers';
import CloseButtonIcon from '@components/common/CloseButtonIcon';
import { IAbstractRecord } from '@models';
import { ThemeProvider } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MAIN_THEME } from '@styles';
import { createMuiThemeFromCustomTheme } from '@styles/themeAdapter';
import { CDN_URL } from '@utils';
import {
  resolveGraphqlBaseUrl,
  resolveIdentityBaseUrl
} from '@utils/tenant-utils';
import { clearUserTokens, getUserToken } from '@utils/userUtils';

import { useAppDispatch } from './core/redux/hooks';
import { SignoutUserAction } from './core/redux/user/actions';
import Portal from './core/routes/Portal';
import DataService from './core/services/DataService';

import { makeStyles } from 'tss-react/mui';

const muiTheme = createMuiThemeFromCustomTheme();

const useStyles = makeStyles()(() => ({
  toastClass: {
    borderRadius: '4px',
    fontSize: MAIN_THEME.fontSize.sm,
    minWidth: '316px',
    minHeight: '50px',
    padding: 15,
    display: 'grid',
    gridTemplateColumns: 'auto auto',
    gridGap: '10px',
    '&.Toastify__toast--success': {
      backgroundColor: MAIN_THEME.color.white,
      color: MAIN_THEME.color.black
    },
    '&.Toastify__toast--info': {
      backgroundColor: MAIN_THEME.color.black
    },
    '&.Toastify__toast--error': {
      backgroundColor: MAIN_THEME.color.red
    },
    '&.Toastify__toast--warning': {
      backgroundColor: MAIN_THEME.color.black
    }
  },
  bodyToastClass: {
    '&.Toastify__toast-body': {
      display: 'grid',
      gridGap: '10px',
      gridTemplateColumns: 'auto auto',
      alignItems: 'baseline'
    }
  },
  closeButtonClass: {
    color: 'inherit',
    background: 'none',
    border: 0,
    cursor: 'pointer',
    outline: 0
  }
}));

class EmptyLink extends ApolloLink {
  constructor() {
    super();
  }

  request(operation: Operation): Observable<FetchResult> {
    return new Observable((observer) => {
      observer.next(operation);
      observer.complete();
    });
  }
}

export const App: React.FC = () => {
  const [booted, setBooted] = useState<boolean>(false);
  const { classes } = useStyles();
  const dispatch = useAppDispatch();

  useEffect(() => {
    classes;
    initializeApp();
  }, []);

  const httpLink = new HttpLink({
    uri: `${resolveGraphqlBaseUrl()}/graphql`
  });

  const setAuthHeaderLink = setContext(() => {
    return {
      headers: {
        authorization: `Bearer ${getUserToken()}` || null
      }
    };
  });

  const errorLink = onError(({ networkError }) => {
    if (
      networkError &&
      'statusCode' in networkError &&
      (networkError as IAbstractRecord).statusCode === 401
    ) {
      clearUserTokens();
      window.location.replace('/login');
    }
  });

  const operationNameLink = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        'x-apollo-operation-name': operation.operationName || 'UnnamedOperation'
      }
    }));

    return forward(operation);
  });
  const link = from([
    setAuthHeaderLink,
    operationNameLink,
    errorLink,
    httpLink
  ]);
  const client = new ApolloClient({
    link,
    cache: new InMemoryCache()
  });

  const initializeApp: () => Promise<void> = async () => {
    DataService.setBaseUrl(resolveIdentityBaseUrl());
    DataService.setLogout(() => {
      dispatch(SignoutUserAction());
    });

    document.head.insertAdjacentHTML(
      'beforeend',
      `
      <link rel="manifest" href="${CDN_URL}/favicons/manifest.json" />
      <link rel="shortcut icon" href="${CDN_URL}/favicons/favicon.ico" />
      <link rel="apple-touch-icon" sizes="180x180" href="${CDN_URL}/favicons/apple-touch-icon.png">
      <meta name="msapplication-TileImage" content="${CDN_URL}/favicons/ms-icon-144x144.png">
      `
    );
    setBooted(true);
  };

  return booted ? (
    <ApolloProvider client={client}>
      <ThemeProvider theme={muiTheme}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DrawerProvider>
            <CssBaseline />
            <GlobalLoader />
            <Portal />
          </DrawerProvider>
        </LocalizationProvider>
        <CssBaseline />
        <ToastContainer
          position="top-center"
          autoClose={3000}
          hideProgressBar
          newestOnTop
          closeOnClick
          draggable
          pauseOnHover
          closeButton={<CloseButtonIcon className={classes.closeButtonClass} />}
          toastClassName={classes.toastClass}
          bodyClassName={classes.bodyToastClass}
        />
      </ThemeProvider>
    </ApolloProvider>
  ) : (
    <></>
  );
};

export default App;
